import { useCallback, useState } from "react";

import type { LocalDisponivel } from "@/modules/carga-de-trabalho";
import {
  JUSTIFICATIVA_GERAL_MAX,
  SIMULACAO_APLICAR_LABEL,
  SIMULACAO_CONFLITO_RESOLVIDO,
  SIMULACAO_CONFLITO_TRATADO,
} from "@/modules/recursos/simulacao";

import { useFocoPrimeiroInvalido } from "../../hooks/use-foco-primeiro-invalido";
import { useRolarAteErro } from "../../hooks/use-rolar-ate-erro";
import { useIgnorarConflitoLocal, useRemanejarLocal } from "../../hooks/use-acoes-conflito-local";
import type { SelecionarLocalErrors } from "./components/selecionar-local-step";
import {
  ESCOLHA_REQUIRED_MESSAGE,
  IGNORADO_SUCCESS_MESSAGE,
  IGNORAR_GUIDANCE,
  JUSTIFICATIVA_REQUIRED_MESSAGE,
  RESOLUCAO_OPTIONS,
  RESOLVER_TITLE,
  SELECIONAR_CONFLITO_TITLE,
} from "./resolver-conflito-local.constants";
import type { ResolucaoTipo } from "./resolver-conflito-local.constants";
import {
  conflitoKey,
  descreverSobreposicao,
  describeConflitoOption,
  formatPeriodo,
  obraLabel,
  obrasDoConflito,
  remanejadoNotice,
  validateJustificativa,
  validateLocal,
  validateObra,
} from "./resolver-conflito-local.utils";
import type {
  ResolverConflitoLocalDialogProps,
  ResolverConflitoLocalDialogViewModel,
  ResolverConflitoLocalEtapa,
  ResolverConflitoLocalPrimario,
} from "./types-resolver-conflito-local";

const SEM_ERROS: SelecionarLocalErrors = { obra: null, local: null };

/**
 * Modal "Resolver Conflito de Alocação" (HU 2042). Com mais de um conflito, o usuário
 * escolhe qual tratar; depois decide entre Remanejar Local (Selecionar Local → Revisão)
 * e Ignorar Conflito. Nenhuma requisição de escrita sai com campo obrigatório vazio.
 */
export function useResolverConflitoLocalDialog({
  local,
  conflitos,
  data,
  periodo,
  today,
  onResolved,
  onClose,
  simulacao,
}: ResolverConflitoLocalDialogProps): ResolverConflitoLocalDialogViewModel {
  const [etapa, setEtapa] = useState<ResolverConflitoLocalEtapa>(
    conflitos.length > 1 ? "conflito" : "proposta",
  );
  const [conflitoIndex, setConflitoIndex] = useState(0);
  const [tratamento, setTratamento] = useState<ResolucaoTipo | null>(null);
  const [tratamentoError, setTratamentoError] = useState<string | null>(null);
  const [alocacaoId, setAlocacaoId] = useState<string | null>(null);
  const [substituto, setSubstituto] = useState<LocalDisponivel | null>(null);
  const [search, setSearch] = useState("");
  const [stepErrors, setStepErrors] = useState<SelecionarLocalErrors>(SEM_ERROS);
  const [justificativa, setJustificativa] = useState("");
  const [justificativaTouched, setJustificativaTouched] = useState(false);
  const [justificativaServerError, setJustificativaServerError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { corpoRef, focarPrimeiroInvalido } = useFocoPrimeiroInvalido();
  const erroRef = useRolarAteErro(serverError);
  const { remanejar, isSubmitting } = useRemanejarLocal();
  const { ignorar } = useIgnorarConflitoLocal();

  const conflito = conflitos[conflitoIndex];
  const alocacao = conflito
    ? (obrasDoConflito(conflito).find((item) => item.id === alocacaoId) ?? null)
    : null;

  const justificativaRequired = validateJustificativa(justificativa);

  // Intenções que o passo Selecionar usa em efeito: identidade estável.
  const escolherObra = useCallback((id: string) => {
    setAlocacaoId(id);
    setStepErrors((atual) => ({ ...atual, obra: null }));
  }, []);
  const escolherSubstituto = useCallback((item: LocalDisponivel) => {
    setSubstituto(item);
    setStepErrors((atual) => ({ ...atual, local: null }));
  }, []);
  const limparSubstituto = useCallback(() => setSubstituto(null), []);

  function fechar() {
    if (!isSubmitting) onClose();
  }

  function escolherTratamento(valor: ResolucaoTipo) {
    setTratamento(valor);
    setTratamentoError(null);
  }

  function avancarProposta() {
    if (!tratamento) {
      setTratamentoError(ESCOLHA_REQUIRED_MESSAGE);
      focarPrimeiroInvalido();
      return;
    }
    setEtapa(tratamento === "remanejar" ? "selecionar" : "ignorar");
  }

  function avancarSelecionar() {
    const errors: SelecionarLocalErrors = {
      obra: validateObra(alocacao?.id ?? null),
      local: validateLocal(substituto?.id ?? null),
    };
    setStepErrors(errors);
    if (errors.obra || errors.local) {
      focarPrimeiroInvalido();
      return;
    }
    setServerError(null);
    setEtapa("revisao");
  }

  function voltarParaProposta() {
    setStepErrors(SEM_ERROS);
    setEtapa("proposta");
  }

  function voltarParaSelecionar() {
    setServerError(null);
    setEtapa("selecionar");
  }

  async function confirmarRemanejamento() {
    if (isSubmitting || !alocacao || !substituto) return;
    setJustificativaTouched(true);
    if (justificativaRequired) {
      focarPrimeiroInvalido();
      return;
    }
    // Com `simulacao`, nunca grava: sem conflito, não faz nada.
    if (simulacao) {
      if (!conflito) return;
      setServerError(null);
      const resultado = await simulacao.remanejar({
        conflito,
        alocacao,
        substituto,
        justificativa: justificativa.trim(),
      });
      if (resultado.ok) onResolved({ severity: "success", summary: SIMULACAO_CONFLITO_RESOLVIDO });
      else if (resultado.mensagem) setServerError(resultado.mensagem);
      return;
    }

    setJustificativaServerError(null);
    setServerError(null);
    const result = await remanejar({
      alocacaoId: alocacao.id,
      localSubstitutoId: substituto.id,
      justificativa: justificativa.trim(),
    });
    if (result.ok) {
      onResolved(remanejadoNotice(alocacao, substituto, result.data.novoConflito));
      return;
    }
    if (result.kind === "invalid") {
      setJustificativaServerError(result.message);
    } else if (result.kind === "localIndisponivel") {
      // O local foi ocupado entre a busca e a confirmação: volta à seleção para escolher outro.
      setSubstituto(null);
      setServerError(result.message);
      setEtapa("selecionar");
    } else {
      setServerError(result.message);
    }
  }

  function primarioDaEtapa(): ResolverConflitoLocalPrimario | null {
    if (!conflito) return null;
    const botao = (rotulo: string, testId: string, acionar: () => void) => ({
      rotulo,
      testId,
      acionar,
      carregando: false,
      desabilitado: false,
    });
    switch (etapa) {
      case "conflito":
        return botao("Avançar", "resolver-conflito-avancar", () => setEtapa("proposta"));
      case "proposta":
        return botao(
          simulacao && tratamento === "ignorar" ? "Salvar" : "Avançar",
          "resolver-conflito-avancar",
          avancarProposta,
        );
      case "selecionar":
        return botao("Próximo Passo", "resolver-conflito-proximo", avancarSelecionar);
      case "revisao":
        return {
          ...botao(
            simulacao ? SIMULACAO_APLICAR_LABEL : "Confirmar Mudança",
            "resolver-conflito-confirmar",
            () => void confirmarRemanejamento(),
          ),
          carregando: isSubmitting,
          desabilitado: Boolean(simulacao && justificativaRequired),
        };
      default:
        return null;
    }
  }

  let voltar: (() => void) | null = null;
  if (conflito && etapa === "selecionar") voltar = voltarParaProposta;
  if (conflito && etapa === "revisao") voltar = voltarParaSelecionar;

  const cidadeUf = [local.cidade, local.uf].filter(Boolean).join(" - ") || "-";

  return {
    etapa,
    titulo: etapa === "conflito" ? SELECIONAR_CONFLITO_TITLE : RESOLVER_TITLE,
    simulando: Boolean(simulacao),
    isSubmitting,
    semConflito: !conflito,
    serverError,
    resumo: conflito
      ? {
          nome: local.nome,
          cidadeUf,
          obras: [conflito.a, conflito.b].map((item) => ({
            key: item.id,
            titulo: `Obra ${obraLabel(item)}`,
            intervalo: formatPeriodo(item.inicio, item.fim),
          })),
          sobreposicao: descreverSobreposicao(conflito),
        }
      : null,
    escolha: {
      total: conflitos.length,
      opcoes: conflitos.map((item, index) => ({
        key: conflitoKey(item),
        titulo: describeConflitoOption(item).title,
        descricao: `Alocado em 2 obras. Sobreposição: ${descreverSobreposicao(item)}`,
        marcada: index === conflitoIndex,
      })),
      escolher: (index) => {
        setConflitoIndex(index);
        setAlocacaoId(null);
        setSubstituto(null);
      },
    },
    proposta: {
      opcoes: RESOLUCAO_OPTIONS.map((opcao) => ({
        ...opcao,
        testId: `resolver-conflito-opcao-${opcao.value}`,
        icone: opcao.value === "remanejar" ? "pi pi-times-circle" : "pi pi-eye-slash",
        recomendada: opcao.value === "remanejar",
        marcada: tratamento === opcao.value,
      })),
      erro: tratamentoError,
      escolher: escolherTratamento,
    },
    selecionar:
      etapa === "selecionar" && conflito
        ? {
            local,
            conflito,
            alocacaoId,
            substituto,
            search,
            errors: stepErrors,
            escolherObra,
            escolherSubstituto,
            limparSubstituto,
            buscar: setSearch,
          }
        : null,
    revisao:
      etapa === "revisao" && conflito && alocacao && substituto
        ? {
            local,
            alocacao,
            substituto,
            data,
            periodo,
            today,
            disabled: isSubmitting,
            justificativa: {
              value: justificativa,
              error:
                justificativaServerError ?? (justificativaTouched ? justificativaRequired : null),
              maxLength: simulacao ? JUSTIFICATIVA_GERAL_MAX : undefined,
              alterar: (valor) => {
                setJustificativa(valor);
                setJustificativaServerError(null);
              },
            },
          }
        : null,
    corpoRef,
    erroRef,
    ignorar:
      etapa === "ignorar" && conflito
        ? {
            descricao: IGNORAR_GUIDANCE,
            mensagemObrigatoria: JUSTIFICATIVA_REQUIRED_MESSAGE,
            onCancel: onClose,
            confirmLabel: simulacao ? SIMULACAO_APLICAR_LABEL : undefined,
            maxLength: simulacao ? JUSTIFICATIVA_GERAL_MAX : undefined,
            onConfirm: async (texto) => {
              // Modo Simulação: a justificativa da ação segue no cenário (comentários da 2224 e 2221).
              if (simulacao) {
                const resultado = await simulacao.ignorar({ conflito, justificativa: texto });
                if (resultado.ok)
                  onResolved({ severity: "success", summary: SIMULACAO_CONFLITO_TRATADO });
                else if (resultado.mensagem)
                  return { kind: "error" as const, message: resultado.mensagem };
                return;
              }
              const result = await ignorar({
                localId: local.id,
                conflitoInicio: conflito.inicio,
                conflitoFim: conflito.fim,
                alocacaoIds: [conflito.a.id, conflito.b.id],
                justificativa: texto,
              });
              if (result.ok) {
                onResolved({ severity: "success", summary: IGNORADO_SUCCESS_MESSAGE });
                return;
              }
              return result;
            },
          }
        : null,
    rodape: { voltar, primario: primarioDaEtapa() },
    fechar,
  };
}
